// -------------------------------
// POLICY INTERFACES
// -------------------------------
export interface PendingPolicy {
    name?: string;
    description?: string;
    file?: File;
    status?: string;
}

export interface PendingDeletePolicy {
    awaitingName?: boolean;
}

export const defaultPolicyStatus = "active";

// -------------------------------
// CREATE POLICY — START
// -------------------------------
export const startCreatePolicyFlow = (
    setPendingPolicy: (data: PendingPolicy | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    nameFromIntent?: string
) => {
    if (nameFromIntent) {
        setPendingPolicy({ name: nameFromIntent, status: defaultPolicyStatus });

        pushAIChat(
            `Got it! Creating policy "${nameFromIntent}". Would you like to add a description?`
        );
        speak(`Creating policy ${nameFromIntent}. Would you like to add a description?`);
        return;
    }

    setPendingPolicy({});
    pushAIChat("Sure! What should the policy name be?");
    speak("What should the policy name be?");
};

// -------------------------------
// CREATE POLICY — PROCESS
// -------------------------------
export const processPendingPolicy = (
    text: string,
    pendingPolicy: PendingPolicy,
    setPendingPolicy: (data: PendingPolicy | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {

    // STEP 1 — Policy Name
    if (!pendingPolicy.name) {
        const cleanName = text.replace(/[^a-zA-Z0-9 ]/g, "").trim();

        if (!cleanName) {
            pushAIChat("Please provide a valid policy name.");
            speak("Please provide a valid policy name.");
            return;
        }

        setPendingPolicy({ ...pendingPolicy, name: cleanName });

        pushAIChat(`Great! Please provide a description for "${cleanName}".`);
        speak(`Please provide a description for ${cleanName}.`);
        return;
    }

    // STEP 2 — Description
    if (!pendingPolicy.description) {
        setPendingPolicy({ ...pendingPolicy, description: text });

        pushAIChat("Should this policy be active or inactive?");
        speak("Should this policy be active or inactive?");
        return;
    }

    // STEP 3 — Status
    if (!pendingPolicy.status) {
        const status = text.toLowerCase().includes("inactive") ? "inactive" : "active";
        setPendingPolicy({ ...pendingPolicy, status });

        pushAIChat("Please upload the policy document (PDF).");
        speak("Please upload the policy document.");

        window.dispatchEvent(new CustomEvent("ai-open-file-upload"));
        return;
    }

    // STEP 4 — File
    if (!pendingPolicy.file) {
        // wait silently for file upload
        return;
    }

    pushAIChat(`Perfect! Creating policy "${pendingPolicy.name}"...`);
    speak(`Creating policy ${pendingPolicy.name}.`);

    window.dispatchEvent(
        new CustomEvent("ai-create-policy", {
            detail: {
                name: pendingPolicy.name,
                description: pendingPolicy.description,
                status: pendingPolicy.status,
                file: pendingPolicy.file,
            },
        })
    );

    setPendingPolicy(null);

};

// -------------------------------
// DELETE POLICY — START
// -------------------------------
export const startDeletePolicyFlow = (
    setPendingDelete: (data: PendingDeletePolicy | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    nameFromIntent?: string
) => {
    if (nameFromIntent) {
        pushAIChat(`Deleting policy "${nameFromIntent}"...`);
        speak(`Deleting policy ${nameFromIntent}`);

        window.dispatchEvent(
            new CustomEvent("ai-delete-policy", {
                detail: { name: nameFromIntent }
            })
        );
        return;
    }

    setPendingDelete({ awaitingName: true });
    pushAIChat("Which policy would you like to delete?");
    speak("Which policy would you like to delete?");
};

// -------------------------------
// DELETE POLICY — PROCESS
// -------------------------------
export const processPendingDeletePolicy = (
    text: string,
    pendingDelete: PendingDeletePolicy,
    setPendingDelete: (data: PendingDeletePolicy | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {
    if (pendingDelete.awaitingName) {
        const cleanName = text.replace(/[^a-zA-Z0-9 ]/g, "").trim();

        if (!cleanName) {
            pushAIChat("Please provide a valid policy name.");
            speak("Please provide a valid policy name.");
            return;
        }

        pushAIChat(`Deleting policy "${cleanName}"...`);
        speak(`Deleting policy ${cleanName}`);

        window.dispatchEvent(
            new CustomEvent("ai-delete-policy", {
                detail: { name: cleanName }
            })
        );

        setPendingDelete(null);
    }
};
